/**
 * React Query hooks for Annotation Task Management
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { annotationBatchApi, annotationTaskApi, annotationExportApi, annotationStatsApi } from '@/lib/api/annotations';
import type {
  BatchCreate,
  BatchUpdate,
  BatchStatus,
  TaskStatus,
  LabelCreate,
  ExportPreviewRequest,
  ExportExecuteRequest,
  GoldenExportRequest,
} from '@/types/annotation';

// ============================================================================
// Query Keys
// ============================================================================

export const annotationKeys = {
  all: ['annotations'] as const,
  batches: () => [...annotationKeys.all, 'batches'] as const,
  batchList: (status?: BatchStatus) => [...annotationKeys.batches(), 'list', { status }] as const,
  batch: (id: string) => [...annotationKeys.batches(), 'detail', id] as const,
  tasks: () => [...annotationKeys.all, 'tasks'] as const,
  taskList: (params?: { batchId?: string; status?: TaskStatus }) =>
    [...annotationKeys.tasks(), 'list', params] as const,
  task: (id: string) => [...annotationKeys.tasks(), 'detail', id] as const,
  myQueue: () => [...annotationKeys.tasks(), 'my-queue'] as const,
  stats: () => [...annotationKeys.all, 'stats'] as const,
  exportPreview: (request: ExportPreviewRequest) => [...annotationKeys.all, 'export-preview', request] as const,
};

// ============================================================================
// Batch Hooks
// ============================================================================

export function useBatches(status?: BatchStatus) {
  return useQuery({
    queryKey: annotationKeys.batchList(status),
    queryFn: () => annotationBatchApi.list({ status, limit: 100 }),
    select: (data) => data.batches,
  });
}

export function useBatch(batchId: string) {
  return useQuery({
    queryKey: annotationKeys.batch(batchId),
    queryFn: () => annotationBatchApi.get(batchId),
    enabled: !!batchId,
  });
}

export function useCreateBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BatchCreate) => annotationBatchApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: annotationKeys.batches() });
    },
  });
}

export function useUpdateBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ batchId, data }: { batchId: string; data: BatchUpdate }) =>
      annotationBatchApi.update(batchId, data),
    onSuccess: (_, { batchId }) => {
      queryClient.invalidateQueries({ queryKey: annotationKeys.batches() });
      queryClient.invalidateQueries({ queryKey: annotationKeys.batch(batchId) });
    },
  });
}

export function useActivateBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (batchId: string) => annotationBatchApi.activate(batchId),
    onSuccess: (_, batchId) => {
      queryClient.invalidateQueries({ queryKey: annotationKeys.batches() });
      queryClient.invalidateQueries({ queryKey: annotationKeys.batch(batchId) });
    },
  });
}

export function useArchiveBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (batchId: string) => annotationBatchApi.archive(batchId),
    onSuccess: (_, batchId) => {
      queryClient.invalidateQueries({ queryKey: annotationKeys.batches() });
      queryClient.invalidateQueries({ queryKey: annotationKeys.batch(batchId) });
    },
  });
}

export function useRestoreBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (batchId: string) => annotationBatchApi.restore(batchId),
    onSuccess: (_, batchId) => {
      queryClient.invalidateQueries({ queryKey: annotationKeys.batches() });
      queryClient.invalidateQueries({ queryKey: annotationKeys.batch(batchId) });
    },
  });
}

export function useDeleteBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (batchId: string) => annotationBatchApi.delete(batchId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: annotationKeys.batches() });
    },
  });
}

// ============================================================================
// Task Hooks
// ============================================================================

export function useTasks(params?: { batchId?: string; status?: TaskStatus }) {
  return useQuery({
    queryKey: annotationKeys.taskList(params),
    queryFn: () =>
      annotationTaskApi.list({
        batch_id: params?.batchId,
        status: params?.status,
        limit: 100,
      }),
    select: (data) => data.tasks,
  });
}

export function useTask(taskId: string) {
  return useQuery({
    queryKey: annotationKeys.task(taskId),
    queryFn: () => annotationTaskApi.get(taskId),
    enabled: !!taskId,
  });
}

export function useMyQueue(limit?: number) {
  return useQuery({
    queryKey: annotationKeys.myQueue(),
    queryFn: () => annotationTaskApi.getMyQueue(limit),
    select: (data) => data.tasks,
  });
}

export function useNextTask(batchId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => annotationTaskApi.getNext(batchId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: annotationKeys.myQueue() });
      queryClient.invalidateQueries({ queryKey: annotationKeys.tasks() });
    },
  });
}

export function useAssignTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ taskId, userId }: { taskId: string; userId?: string }) =>
      annotationTaskApi.assign(taskId, { user_id: userId }),
    onSuccess: (_, { taskId }) => {
      queryClient.invalidateQueries({ queryKey: annotationKeys.tasks() });
      queryClient.invalidateQueries({ queryKey: annotationKeys.task(taskId) });
      queryClient.invalidateQueries({ queryKey: annotationKeys.myQueue() });
    },
  });
}

export function useSkipTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (taskId: string) => annotationTaskApi.skip(taskId),
    onSuccess: (_, taskId) => {
      queryClient.invalidateQueries({ queryKey: annotationKeys.tasks() });
      queryClient.invalidateQueries({ queryKey: annotationKeys.task(taskId) });
      queryClient.invalidateQueries({ queryKey: annotationKeys.myQueue() });
      queryClient.invalidateQueries({ queryKey: annotationKeys.batches() });
    },
  });
}

export function useSubmitLabel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ taskId, data }: { taskId: string; data: LabelCreate }) =>
      annotationTaskApi.submit(taskId, data),
    onSuccess: (_, { taskId }) => {
      queryClient.invalidateQueries({ queryKey: annotationKeys.tasks() });
      queryClient.invalidateQueries({ queryKey: annotationKeys.task(taskId) });
      queryClient.invalidateQueries({ queryKey: annotationKeys.myQueue() });
      queryClient.invalidateQueries({ queryKey: annotationKeys.batches() });
      queryClient.invalidateQueries({ queryKey: annotationKeys.stats() });
    },
  });
}

// ============================================================================
// Export Hooks
// ============================================================================

export function useExportPreview() {
  return useMutation({
    mutationFn: (data: ExportPreviewRequest) => annotationExportApi.preview(data),
  });
}

export function useExecuteExport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ExportExecuteRequest) => annotationExportApi.execute(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: annotationKeys.batches() });
    },
  });
}

// ============================================================================
// Stats Hooks
// ============================================================================

export function useAnnotationStats() {
  return useQuery({
    queryKey: annotationKeys.stats(),
    queryFn: () => annotationStatsApi.getAll(),
  });
}

export function useExportGolden() {
  return useMutation({
    mutationFn: (data: GoldenExportRequest) => annotationStatsApi.exportGolden(data),
  });
}
